import { authService } from './api/auth';
import { usersService } from './api/users';
import { clubsService } from './api/clubs';
import { matchesService } from './api/matches';
import { adminService } from './api/admin';
import { messagesService } from './api/messages';
import { systemService } from './api/system';
import { notificationsService } from './api/notifications';
import { managerService } from './api/manager';
import { showroomService } from './api/showroom';
import { plansService } from './api/plans';
import { pushService } from './api/push';
import { courtsService } from './api/courts';
import { devicesService } from './api/devices';
import { planningService } from './api/planning';
import { pricingService } from './api/pricing';
import { healthService } from './api/health';
import { circlesService } from './api/circles';
import { calendarService } from './api/calendar';
import { feedService } from './api/feed';
import { paymentService } from './api/payment';
import { tournamentsService } from './api/tournaments'; // [NEW]
import { sponsorsService } from './api/sponsors'; // [NEW]
import { refereeService } from './api/referee'; // [NEW]
import { marketplaceService } from './api/marketplace';
import { podService } from './api/pod';
import { federationsService } from './api/federations';
import { proService } from './api/pro';
import { academyService } from './api/academy'; // [NEW]
import { API_URL } from './api/config';


export * from './api/types';
export * from './api/config';
export type { Court } from './api/courts';

export const api = {
    auth: authService,
    users: usersService,
    federations: federationsService,
    getStripeStatus: async (clubId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/clubs/${clubId}/stripe/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch Stripe status");
        return res.json();
    },
    onboardStripe: async (clubId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/clubs/${clubId}/stripe/onboard`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({})
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to start onboarding");
        }
        return res.json();
    },
    toggleStripeConnect: async (clubId: string, enabled: boolean) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/clubs/${clubId}/stripe/toggle`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ enabled })
        });
        if (!res.ok) throw new Error("Failed to toggle Stripe Connect");
        return res.json();
    },
    clubs: clubsService,
    academy: academyService,
    matches: matchesService,
    admin: adminService,
    messages: {
        list: (matchId: number | string) => messagesService.list(String(matchId)), // Assuming messagesService has a list method
        send: (matchId: number | string, userId: string, content: string) => messagesService.send(String(matchId), userId, content), // Assuming messagesService has a send method
        listForCircle: (circleId: string) => messagesService.listForCircle(circleId), // Assuming messagesService has a listForCircle method
        sendForCircle: (circleId: string, userId: string, content: string) => messagesService.sendForCircle(circleId, userId, content), // Assuming messagesService has a sendForCircle method
        // Direct Messages
        createConversation: (otherUserId: string, clubId?: number) => messagesService.createConversation(otherUserId, clubId), // Assuming messagesService has createConversation
        createSupport: (clubId: string) => messagesService.createSupport(clubId),
        listConversations: () => messagesService.listConversations(), // Assuming messagesService has listConversations
        listDirect: (conversationId: string) => messagesService.listDirect(conversationId), // Assuming messagesService has listDirect
        sendDirect: (conversationId: string, userId: string, content: string) => messagesService.sendDirect(conversationId, userId, content), // Assuming messagesService has sendDirect
        markAsRead: (conversationId: string) => messagesService.markAsRead(conversationId)
    },
    system: systemService,
    notifications: notificationsService,
    push: pushService,
    manager: managerService,
    pricing: pricingService,
    showroom: showroomService,
    plans: plansService,
    courts: courtsService,
    planning: planningService,
    devices: devicesService,
    health: healthService,
    circles: circlesService,
    calendar: calendarService,
    feed: feedService,
    payment: paymentService,
    tournaments: tournamentsService,
    sponsors: sponsorsService, // [NEW]
    referee: refereeService, // [NEW]
    marketplace: marketplaceService,
    pod: podService,
    pro: proService,
    discovery: {
        getNearby: (lat?: number, lon?: number) => api.get(`/discovery/nearby${lat && lon ? `?lat=${lat}&lon=${lon}` : ''}`),
        swipe: (toUserId: string, action: 'LIKE' | 'DISLIKE') => api.post('/discovery/swipe', { to_user_id: toUserId, action }),
        getProfile: () => api.get('/discovery/profile'),
        updateProfile: (data: any) => api.put('/discovery/profile', data),
        getMatches: () => api.get('/discovery/matches'),
        markMatchesAsRead: () => api.post('/discovery/matches/read', {}),
        propose: (data: any) => api.post('/discovery/propose', data),
        getProposal: (id: string) => api.get(`/discovery/proposals/${id}`),
        respondToProposal: (id: string, status: 'ACCEPTED' | 'DECLINED') => api.post(`/discovery/proposals/${id}/respond`, { status }),
        withdrawProposal: (id: string) => api.post(`/discovery/proposals/${id}/withdraw`, {}),
        unmatch: (matchId: string) => api.delete(`/discovery/matches/${matchId}`)
    },
    get: async (endpoint: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}${endpoint}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Request failed");
        return res.json();
    },
    post: async (endpoint: string, body: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error("Request failed");
        return res.json();
    },
    put: async (endpoint: string, body: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}${endpoint}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error("Request failed");
        return res.json();
    },
    patch: async (endpoint: string, body: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}${endpoint}`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error("Request failed");
        return res.json();
    },
    delete: async (endpoint: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}${endpoint}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Request failed");
        return res.json();
    }
};
